import { useCallback, useEffect, useRef, useState } from "react";
import type {
  RavenMessage,
  RavenRouteChangePayload,
  RavenErrorPayload,
  RavenNavigateParentPayload,
} from "./types";
import { RAVEN_MESSAGES } from "./types";
import { useAuthStore } from "@/lib/store/authStore";
import { trackEvent } from "@/lib/activity-tracker/tracker";

interface UseRavenMessagingOptions {
  onReady?: () => void;
  onRouteChange?: (path: string, title: string) => void;
  onError?: (error: string) => void;
  onNavigateParent?: (action: string) => void;
}

// Calculate the Frappe origin for cross-origin iframe communication
// GOTCHA G2: Must match CrmEmbed/BuilderEmbed/InsightsEmbed exactly
// In control_plane mode, use tenant server URL instead of Frappe origin
function getFrappeOrigin(authMode: string | undefined, tenantServerUrl: string | null): string {
  // In control_plane mode, messages come from tenant server
  if (authMode === 'control_plane' && tenantServerUrl) {
    return new URL(tenantServerUrl).origin;
  }

  const envUrl = import.meta.env.VITE_FRAPPE_URL;
  if (envUrl) {
    return new URL(envUrl).origin;
  }
  // In development, Frappe runs on port 8000
  if (window.location.port === "5173") {
    return `${window.location.protocol}//${window.location.hostname}:8000`;
  }
  return window.location.origin;
}

export function useRavenMessaging(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  options: UseRavenMessagingOptions
) {
  const [isReady, setIsReady] = useState(false);

  // Get auth mode and tenant server URL for origin calculation
  const authMode = useAuthStore((state) => state.authMode);
  const tenantServerUrl = useAuthStore((state) => state.tenantServerUrl);
  const frappeOriginRef = useRef(getFrappeOrigin(authMode, tenantServerUrl));

  // Update origin ref when auth mode or tenant server URL changes
  useEffect(() => {
    frappeOriginRef.current = getFrappeOrigin(authMode, tenantServerUrl);
  }, [authMode, tenantServerUrl]);

  // Store options in ref to avoid stale closures (optionsRef pattern)
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Send message to iframe
  const sendMessage = useCallback(
    (type: string, payload?: unknown) => {
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow) {
        console.warn("[RavenEmbed] iframe not available");
        return;
      }

      const message: RavenMessage = { type, payload, source: "react-parent" };
      iframe.contentWindow.postMessage(message, frappeOriginRef.current);
    },
    [iframeRef]
  );

  // Handle incoming messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Security: Only accept messages from Frappe origin
      if (event.origin !== frappeOriginRef.current) {
        return;
      }

      const data = event.data as RavenMessage;
      if (!data || typeof data.type !== "string") {
        return;
      }

      // Validate source is from Raven (not CRM or other embeds)
      if (data.source !== "frappe-raven") {
        return;
      }

      const { type, payload } = data;
      console.log("[RavenEmbed] Received message:", type, payload);

      switch (type) {
        case RAVEN_MESSAGES.READY: {
          setIsReady(true);
          trackEvent({ event_type: 'iframe_focus', metadata: { iframe: 'raven' } });
          optionsRef.current.onReady?.();
          break;
        }

        case RAVEN_MESSAGES.ROUTE_CHANGE: {
          const routeData = payload as RavenRouteChangePayload;
          trackEvent({ event_type: 'iframe_focus', page_path: routeData.path, metadata: { iframe: 'raven', title: routeData.title } });
          optionsRef.current.onRouteChange?.(
            routeData.path,
            routeData.title || ""
          );
          break;
        }

        case RAVEN_MESSAGES.ERROR: {
          const errorData = payload as RavenErrorPayload;
          optionsRef.current.onError?.(errorData.message);
          break;
        }

        case RAVEN_MESSAGES.NAVIGATE_PARENT: {
          const navData = payload as RavenNavigateParentPayload;
          optionsRef.current.onNavigateParent?.(navData.action);
          break;
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []); // Empty deps - uses refs for callbacks

  // Reset state when needed
  const reset = useCallback(() => {
    setIsReady(false);
  }, []);

  // Public API methods
  const navigate = useCallback(
    (path: string) => {
      sendMessage(RAVEN_MESSAGES.NAVIGATE, { path });
    },
    [sendMessage]
  );

  return {
    isReady,
    navigate,
    reset,
  };
}
